// Overview of The Algorithm that groups edges for collapsed nodes
// (please try to make sure to keep this up to date since it is already kinda rocket science)
// =============================
// Bullet legend:
// - ordered steps
// * unordered steps (e.g. object properties construction, condition composition)
// > conditional steps (ordered)
// -----------------------------
// - group by source/target nodes
//   * even source->target and target->source fall to one group
//   * try to maintain original source->target order if all collapsed edges have the same direction
//   * if the resulting grouped edge can be unidirected, order source->target to point in that direction
//   * keep direction info for later use
// - for each with same ends:
//   - group by target edge settings id groups (some may already be, some may not be grouped, some may have null group)
//     - split to groups by edgeSettingsId
//     - for all GroupEdgeSettings with grouped: true:
//       > if it contains child edges with more than 1 different edgeSettingsId, change all children's edgeSettingsId
//         to that group
//     - for each group by edgeSettingsId:
//       > grouped:true: return one merged edge
//       > grouped:false: just reconnect their source/target
//
//   - passthru singles, what remains are edges that will be actually merged
//   - for each group perform merge:
//     * id: generate
//     * children: merge from given
//     * edgeSettingsId: id of given group;
//     * labels:
//       > type=protocol:
//         > if all are of same protocol, `${protocol} x ${children.length}`
//         > otherwise `${protocolGroup} x ${children.length}`
//       > other:
//         * type: merge by same values in this field
//         * visible: some merged are visible
//         * text: iff there is the same value for all merged labels, use it, otherwise omit this EdgeLabelValue
//     * positions: will be recalculated
//     * protocol?: if all merged have the same value use it, otherwise keep empty;
//     * shift: will have to be recalculated (keep loopbacks in mind)
//     * source: first grouped by node;
//     * style:
//       * line: from settings
//       * others: not used yet
//     * target: second grouped by node;
//     * type: merge from present

import {
  type EdgeCenterLabel,
  type EdgeCenterLabelValue,
  type EdgeData,
  type EdgeDirection,
  type EdgeLabelValue,
  type EdgeLabels,
  type EdgeLinecapLabel,
  type EdgeLinecapLabelValue,
  GraphConstants,
  type GraphEdgeSettings,
  type GraphEdgeSettingsId,
  type GraphEdgesSettings,
  type GroupEdgeSettings,
  type NodeId,
  type PathLookupEdgeSettings,
  type PathLookupEdgeSettingsId,
  type ProtocolEdgeType,
  type TopologyEdgeSettings,
  type TopologyEdgeSettingsId,
  dataForPositionsFromEdge,
  getEdgePositions,
  getEdgeShift,
  isTopologyEdgeData,
  rgbStringToNumber,
} from '@ipfabric/utils';
import { bindMethod, branch } from '@ipfabric/utils/fp/function';
import { type MapOfLists, mapOfLists } from '@ipfabric/utils/fp/mapOfLists';
import { onlyValue } from '@ipfabric/utils/fp/onlyValue';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import { plainSet } from '@ipfabric/utils/fp/plainSet';
import { prop } from '@ipfabric/utils/fp/prop';
import { isGroupSettings } from 'components/Graph/GraphState';
import { getNextCollapseEdgeId } from 'components/Graph/commands/collapsing/helpers/collapsedEntityId';
import { getEdgeAttribute } from 'components/Graph/helpers/getEdgeAttribute';
import { getEdgeThickness } from 'components/Graph/helpers/getEdgeThickness';
import type {
  CollapsedNodeGroupId,
  GraphData,
  HydratedCollapsedNodeGroup,
} from 'components/Graph/model/GraphData';
import { array, option, record } from 'fp-ts';
import type { Option } from 'fp-ts/lib/Option';
import { constUndefined, flow, pipe } from 'fp-ts/lib/function';

type EdgesByAdjacentNodes = Record<
  NodeId,
  Record<NodeId, { direction: EdgeDirection; edges: EdgeData[] }>
>;
type EdgesToGroup = {
  edges: EdgeData[];
  /**
   * Group settings for already grouped edges, protocol settings for protocol edges
   */
  edgeSettings: GraphEdgeSettings;
  /**
   * Group settings for protocol edges if given protocol is in some group
   */
  groupSettings?: GroupEdgeSettings;
  // following properties betweenNodesOffset and betweenNodesTotal are there to properly
  // calculate shift of the collapse group edges
  /**
   * How many collapse group edges between given nodes are listed before
   */
  betweenNodesOffset: number;
  /**
   * How many collapse group edges between given nodes will exist in total
   */
  betweenNodesTotal: number;
};

/**
 * Take a list of regular edges where one of their ends is hidden in a collapse group
 * and build replacement "Collapse group edges" for them
 *
 * This function is part of complex collapsing nodes process. It has to be called
 * after graph.nodes and graph.collapsedNodeGroups are set in the graph to the state after
 * the collapse, otherwise it would give irrelevant results.
 *
 * The process of collapsing edges itself is also very complicated (as you can see in this file),
 * so use and/or edit this function and related helper functions with caution.
 *
 * For algorithm description see the long comment at the beginning of this module.
 *
 * @internal it needs to be called in a specific context - see the comment above
 */
export const createCollapseGroupEdges =
  (graph: GraphData, edgesSettings: GraphEdgesSettings) =>
  (edgesToCollapse: EdgeData[]): EdgeData[] => {
    return pipe(
      edgesToCollapse,
      groupByAdjacentNodes(graph),
      chainGroupsByAdjacentNodes(
        createCollapseGroupEdgesForAdjacentNodes(graph, edgesSettings),
      ),
    );
  };

const groupByAdjacentNodes =
  (graph: GraphData) =>
  (edges: EdgeData[]): EdgesByAdjacentNodes => {
    const nodesByGroup: Record<NodeId, CollapsedNodeGroupId> = pipe(
      graph.collapsedNodeGroups,
      record.reduce<
        HydratedCollapsedNodeGroup,
        Record<NodeId, CollapsedNodeGroupId>
      >({}, (result, group) => {
        for (const node of Object.values(group.nodes)) {
          result[node.id] = group.id;
        }
        return result;
      }),
    );
    /**
     * Get id of node that currently represents given nodeId:
     * - if it is collapsed in a group, return that group node's id
     * - otherwise just pass-thru given id
     */
    const getRepresentingNodeId = (nodeId: NodeId): NodeId =>
      nodesByGroup[nodeId] ?? nodeId;

    const mergeDirections = (
      t1: EdgeDirection,
      t2: EdgeDirection,
      opposite: boolean,
    ) => {
      // if edges of opposite directions are merged, two directed edges result in bidirected
      if (opposite && t1 === 'directed' && t2 === 'directed') {
        return 'bidirected';
      }
      // return stronger of the two types: bidirected > directed > undirected
      if (t1 === 'bidirected' || t2 === 'bidirected') {
        return 'bidirected';
      }
      if (t1 === 'directed' || t2 === 'directed') {
        return 'directed';
      }
      return 'undirected';
    };

    const addEdgeToResult = (
      result: EdgesByAdjacentNodes,
      source: NodeId,
      target: NodeId,
      edge: EdgeData,
      opposite: boolean,
    ): void => {
      if (!result[source]) {
        result[source] = {};
      }
      // Fixed noUncheckedIndexedAccess NIM-19408
      const s = result[source]!;
      if (!s[target]) {
        s[target] = { direction: 'undirected', edges: [] };
      }
      // Fixed noUncheckedIndexedAccess NIM-19408
      const groupedEdges = s[target]!;
      groupedEdges.direction = mergeDirections(
        groupedEdges.direction,
        edge.direction,
        opposite,
      );
      groupedEdges.edges.push({
        ...edge,
        source: opposite ? target : source,
        target: opposite ? source : target,
      });
    };

    return pipe(
      edges,
      array.reduce<
        EdgeData,
        Record<
          NodeId,
          Record<NodeId, { direction: EdgeDirection; edges: EdgeData[] }>
        >
      >({}, (result, edge) => {
        const source = getRepresentingNodeId(edge.source);
        const target = getRepresentingNodeId(edge.target);
        const opposite = result[target]?.[source];
        if (opposite) {
          // there is already opposite-direction edge
          if (
            opposite.direction === 'undirected' &&
            edge.direction === 'directed'
          ) {
            // flip current source/target to try keeping one-way directed edge
            if (!result[source]) {
              result[source] = {};
            }
            // Fixed noUncheckedIndexedAccess NIM-19408
            result[source]![target] = {
              direction: 'directed',
              edges: [...opposite.edges, { ...edge, source, target }],
            };
            delete result[target]?.[source];
          } else {
            // makes no sense to flip the group's source/target, merge into opposite-directioned group
            addEdgeToResult(result, target, source, edge, true);
          }
        } else {
          addEdgeToResult(result, source, target, edge, false);
        }
        return result;
      }),
    );
  };

/**
 * Transform edgesByAdjacentNodes into a list of new edges using a function
 * that makes a list of edges from each input entry, merging results into
 * a flat output list
 */
const chainGroupsByAdjacentNodes: (
  fn: (
    edges: EdgeData[],
    direction: EdgeDirection,
    source: NodeId,
    target: NodeId,
  ) => EdgeData[],
) => (edgesByAdjacentNodes: EdgesByAdjacentNodes) => EdgeData[] = (fn) =>
  flow(
    record.collect((source, targets) =>
      pipe(
        targets,
        record.collect((target, { edges, direction }) =>
          fn(edges, direction, source, target),
        ),
        array.flatten,
      ),
    ),
    array.flatten,
  );

const groupByTargetSettings =
  (edgesSettings: GraphEdgesSettings) =>
  (edges: EdgeData[]): MapOfLists<GraphEdgeSettingsId, EdgeData> => {
    const edgesBySettingsId: MapOfLists<GraphEdgeSettingsId, EdgeData> = pipe(
      edges,
      mapOfLists.groupArrayBy(prop('edgeSettingsId')),
    );

    const getSettingsIdsWithExistingEdges: (
      settings: (TopologyEdgeSettings | PathLookupEdgeSettings)[],
    ) => Set<TopologyEdgeSettingsId | PathLookupEdgeSettingsId> = flow(
      array.map(prop('id')),
      array.filter(bindMethod(edgesBySettingsId, 'has')),
      plainSet.fromArray,
    );

    const hasGroupMultipleEdgeTypes = (
      childrenSettings: Set<TopologyEdgeSettingsId | PathLookupEdgeSettingsId>,
      edgeSettings: GroupEdgeSettings,
    ) =>
      childrenSettings.size + (edgesBySettingsId.has(edgeSettings.id) ? 1 : 0) >
      1;

    const mergeIntoSettingsId = (
      targetSettingsId: GraphEdgeSettingsId,
    ): ((sourceSettingsId: GraphEdgeSettingsId) => void) =>
      flow(
        plainMap.popFromMutable(edgesBySettingsId),
        option.map((childEdges) => {
          mapOfLists.mergeMutable(
            targetSettingsId,
            childEdges,
          )(edgesBySettingsId);
        }),
      );

    for (const edgeSettings of edgesSettings) {
      if (isGroupSettings(edgeSettings) && edgeSettings.grouped) {
        const childrenSettings = getSettingsIdsWithExistingEdges(
          edgeSettings.children,
        );
        if (hasGroupMultipleEdgeTypes(childrenSettings, edgeSettings)) {
          childrenSettings.forEach(mergeIntoSettingsId(edgeSettings.id));
        }
      }
    }

    return edgesBySettingsId;
  };

const mergeLabelValues = <T extends EdgeCenterLabel | EdgeLinecapLabel>(
  valuesByType: MapOfLists<T, EdgeLabelValue<T>>,
): EdgeLabelValue<T>[] =>
  pipe(
    valuesByType,
    plainMap.filterMapToArray<T, EdgeLabelValue<T>[], EdgeLabelValue<T>>(
      ([type, labelValues]) =>
        pipe(
          labelValues,
          array.map(prop('text')),
          onlyValue(),
          option.map((text) => ({
            text,
            type,
            visible: labelValues.some(prop('visible')),
          })),
        ),
    ),
  );

const mergeLabels = (
  labels: EdgeLabels[],
  edgeSettings: GraphEdgeSettings,
  childrenCount: number,
): EdgeLabels => {
  const labelsByPositionAndType = labels.reduce(
    (result, { source, center, target }) => {
      for (const label of source ?? []) {
        mapOfLists.pushMutable(label.type, label)(result.source);
      }
      for (const label of target ?? []) {
        mapOfLists.pushMutable(label.type, label)(result.target);
      }
      for (const label of center ?? []) {
        if (label.type === 'protocol') {
          result.protocol.push(label);
        } else {
          mapOfLists.pushMutable<EdgeCenterLabel, EdgeCenterLabelValue>(
            label.type,
            label,
          )(result.center);
        }
      }
      return result;
    },
    {
      source: new Map<EdgeLinecapLabel, EdgeLinecapLabelValue[]>(),
      center: new Map<EdgeCenterLabel, EdgeCenterLabelValue[]>(),
      target: new Map<EdgeLinecapLabel, EdgeLinecapLabelValue[]>(),
      // because we treat protocol in a different way than other labels
      protocol: [] as EdgeCenterLabelValue[],
    },
  );
  return {
    source: mergeLabelValues(labelsByPositionAndType.source),
    center: [
      ...mergeLabelValues(labelsByPositionAndType.center),
      {
        type: 'protocol',
        visible: labelsByPositionAndType.protocol.some(flow(prop('visible'))),
        text: `${
          edgeSettings.type === 'group'
            ? edgeSettings.label
            : getEdgeAttribute(edgeSettings).name
        }${childrenCount > 1 ? ` x ${childrenCount}` : ''}`,
      },
    ],
    target: mergeLabelValues(labelsByPositionAndType.target),
  };
};

const getLabelsOrientedFrom =
  (source: NodeId) =>
  ({ labels, source: edgeSource }: EdgeData): Option<EdgeLabels> => {
    if (!labels) {
      return option.none;
    }
    return option.some(
      source === edgeSource
        ? labels
        : {
            source: labels.target,
            center: labels.center,
            target: labels.source,
          },
    );
  };

const createCollapseGroupEdgeForGroupedSettings = (
  graph: GraphData,
  direction: EdgeDirection,
  source: NodeId,
  target: NodeId,
  { edges, edgeSettings, betweenNodesOffset, betweenNodesTotal }: EdgesToGroup,
): EdgeData => {
  const children = pipe(
    edges,
    array.filter(isTopologyEdgeData),
    array.chain(prop('children')),
  );
  const shift = getEdgeShift(betweenNodesOffset, betweenNodesTotal);
  const thickness = getEdgeThickness(
    edgeSettings.style.thicknessThresholds,
    children.length || GraphConstants.EDGE_DEFAULT_THICKNESS,
  );
  const labels = mergeLabels(
    pipe(edges, array.filterMap(getLabelsOrientedFrom(source))),
    edgeSettings,
    children.length,
  );
  return {
    id: getNextCollapseEdgeId(),
    ...(children.length && { children }),
    direction,
    edgeSettingsId: edgeSettings.id,
    labels,
    positions: getEdgePositions(
      // take source/target from edge since proper orientation was updated in `groupByAdjacentNodes`,
      // it may be opposite direction from current function's source/target attributes
      // Fixed noUncheckedIndexedAccess NIM-19408
      graph.nodes[source]!,
      graph.nodes[target]!,
      {
        direction,
        labels,
        shift,
        thickness,
      },
    ),
    protocol: pipe(
      edges,
      array.filterMap(
        flow(
          prop('protocol'),
          // filter out edges without protocols to prevent onlyValue resulting to some(undefined)
          option.fromNullable,
        ),
      ),
      onlyValue(),
      option.getOrElse<ProtocolEdgeType | undefined>(constUndefined),
    ),
    shift,
    source,
    style: {
      // this will probably have to be updated when other style attributes (circle and arrowHeads)
      // will start to be used, now we just assign undefined just to find this place when searching
      // for references/usages
      circle: undefined,
      arrowHeads: undefined,
      line: {
        color: rgbStringToNumber(edgeSettings.style.color),
        pattern: edgeSettings.style.pattern,
        thickness,
      },
    },
    target,
  };
};

const createCollapseGroupEdgesForUngroupedSettings = (
  graph: GraphData,
  { edges, betweenNodesOffset, betweenNodesTotal }: EdgesToGroup,
): EdgeData[] =>
  edges.map((edge, index) => {
    const shift = getEdgeShift(betweenNodesOffset + index, betweenNodesTotal);
    return {
      ...edge,
      id: getNextCollapseEdgeId(),
      positions: getEdgePositions(
        // take source/target from edge since proper orientation was updated in `groupByAdjacentNodes`,
        // it may be opposite direction from current function's source/target attributes
        // Fixed noUncheckedIndexedAccess NIM-19408
        graph.nodes[edge.source]!,
        graph.nodes[edge.target]!,
        {
          ...dataForPositionsFromEdge(edge),
          shift,
        },
      ),
      shift,
    };
  });

const createCollapseGroupEdgesForEdgeSettings =
  (
    graph: GraphData,
    direction: EdgeDirection,
    source: NodeId,
    target: NodeId,
  ) =>
  (edgesToGroup: EdgesToGroup): EdgeData[] =>
    edgesToGroup.edgeSettings.grouped || edgesToGroup.groupSettings?.grouped
      ? [
          createCollapseGroupEdgeForGroupedSettings(
            graph,
            direction,
            source,
            target,
            edgesToGroup,
          ),
        ]
      : createCollapseGroupEdgesForUngroupedSettings(graph, edgesToGroup);

const getFlatEdgesSettings = (
  edgesSettings: GraphEdgesSettings,
): Map<GraphEdgeSettingsId, GraphEdgeSettings> =>
  edgesSettings.reduce((result, edgeSettings) => {
    result.set(edgeSettings.id, edgeSettings);
    if (isGroupSettings(edgeSettings)) {
      for (const childEdgeSettings of edgeSettings.children) {
        result.set(childEdgeSettings.id, childEdgeSettings);
      }
    }
    return result;
  }, new Map());

const createGroupsByProtocolSettingsMap = array.reduce<
  GraphEdgeSettings,
  Map<TopologyEdgeSettingsId | PathLookupEdgeSettingsId, GroupEdgeSettings>
>(new Map(), (result, edgeSettings) => {
  if (isGroupSettings(edgeSettings)) {
    for (const { id } of edgeSettings.children) {
      result.set(id, edgeSettings);
    }
  }
  return result;
});

const edgesToGroupFromMap = (edgesSettings: GraphEdgesSettings) => {
  const groupsByProtocolSettings =
    createGroupsByProtocolSettingsMap(edgesSettings);

  return (
    edgesBySettingsId: MapOfLists<
      TopologyEdgeSettingsId | PathLookupEdgeSettingsId,
      EdgeData
    >,
  ): EdgesToGroup[] => {
    const flatEdgesSettings: Map<
      TopologyEdgeSettingsId | PathLookupEdgeSettingsId,
      GraphEdgeSettings
    > = getFlatEdgesSettings(edgesSettings);
    let offset = 0;
    return pipe(
      edgesBySettingsId,
      plainMap.collect((edgeSettingsId, edges) =>
        pipe(
          flatEdgesSettings,
          plainMap.get(edgeSettingsId),
          option.map(
            flow(
              (edgeSettings) => ({
                edges,
                edgeSettings,
                groupSettings: groupsByProtocolSettings.get(edgeSettings.id),
                betweenNodesOffset: offset,
              }),
              branch(({ edgeSettings }) => {
                // increase offset to render proper shift to later edges;
                // for edges grouped by settings, 1 edge will be rendered; for others, all of them will be rendered
                offset += edgeSettings.grouped ? 1 : edges.length;
              }),
            ),
          ),
        ),
      ),
      array.compact,
      array.map((edgesToGroup) => ({
        ...edgesToGroup,
        betweenNodesTotal: offset,
      })),
    );
  };
};

const createCollapseGroupEdgesForAdjacentNodes = (
  graph: GraphData,
  edgesSettings: GraphEdgesSettings,
) => {
  const prepareEdgesToGroup = flow(
    groupByTargetSettings(edgesSettings),
    edgesToGroupFromMap(edgesSettings),
  );
  return (
    edges: EdgeData[],
    direction: EdgeDirection,
    source: NodeId,
    target: NodeId,
  ): EdgeData[] =>
    pipe(
      edges,
      prepareEdgesToGroup,
      array.chain(
        createCollapseGroupEdgesForEdgeSettings(
          graph,
          direction,
          source,
          target,
        ),
      ),
    );
};

export const testable = {
  chainGroupsByAdjacentNodes,
};
